import { useAppSelector } from "app/redux-store-interactions";
import { useHidden } from "contexts/hidden";
import { useHighlightCollections } from "contexts/highlightCollections";
import { useHighlighted } from "contexts/highlighted";
import { useSelectionBasket } from "contexts/selectionBasket";
import { selectCoordinates } from "features/coordinates";
import { selectMeasureEntities } from "features/measure/measureSlice";
import { selectOutlineLasers } from "features/outlineLaser/outlineLaserSlice";
import { selectPointLines } from "features/pointLine/pointLineSlice";
import { ObjectVisibility, selectClippingPlanes, selectDefaultVisibility } from "features/render";

/**
 * Returns `true` when there is some view state that {@link useResetView} would clear
 * (selections, hidden objects, clipping planes, measurements, etc.). Use this to drive
 * the disabled state of "Reset view" / "Clear view" buttons so they only light up when
 * there is something to reset.
 *
 * Keep this in sync with the state that `useResetView` actually clears.
 */
export function useCanResetView(): boolean {
    const highlighted = useHighlighted();
    const hidden = useHidden();
    const selectionBasket = useSelectionBasket();
    const highlightCollections = useHighlightCollections();
    const defaultVisibility = useAppSelector(selectDefaultVisibility);
    const clippingPlanes = useAppSelector(selectClippingPlanes);
    const coordinates = useAppSelector(selectCoordinates);
    const lasers = useAppSelector(selectOutlineLasers);
    const pointLines = useAppSelector(selectPointLines);
    const dimensions = useAppSelector(selectMeasureEntities);

    return (
        highlighted.idArr.length > 0 ||
        hidden.idArr.length > 0 ||
        selectionBasket.idArr.length > 0 ||
        Object.values(highlightCollections).some((collection) => collection.idArr.length > 0) ||
        defaultVisibility !== ObjectVisibility.Neutral ||
        clippingPlanes.enabled ||
        clippingPlanes.planes.length > 0 ||
        coordinates.length > 0 ||
        lasers.length > 0 ||
        pointLines.some((line) => line.points.length > 0) ||
        dimensions.some((set) => set.length > 0)
    );
}
